import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EventManagement from './EventManagement';
import CompetitionManagement from './CompetitionManagement';
import ScoringComponentManagement from './ScoringComponentManagement';
import CompetitorManagement from './CompetitorManagement';
import TeamManagement from './TeamManagement';
import CompetitionRegistration from './CompetitionRegistration'; // Import the new component
import LeaderboardManagement from './LeaderboardManagement';
import RollupLeaderboardManagement from './RollupLeaderboardManagement';
import AdminLeaderboards from './AdminLeaderboards';
import AdminUserManagement from './AdminUserManagement'; // Import the new component

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const getIsMobile = () => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    const [isMobile, setIsMobile] = useState(getIsMobile());
    const [isSidebarOpen, setIsSidebarOpen] = useState(!getIsMobile());

    // Fetch role information on mount
    useEffect(() => {
        fetch('/.auth/me')
            .then(res => res.json())
            .then(data => {
                const claim = data.clientPrincipal?.claims.find(c => c.typ === 'jobTitle');
                if (claim) setJobTitle(claim.val);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const mobile = getIsMobile();
            setIsMobile(prev => {
                if (prev !== mobile) {
                    setIsSidebarOpen(!mobile);
                }
                return mobile;
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLogout = () => {
        window.location.href = '/.auth/logout'; // Navigate to the homepage after logout
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(prev => !prev);
    };

    const handleHomeClick = () => {
        navigate('/');
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    };

    const handleNavClick = (sectionId) => {
        setActiveSection(sectionId);
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    };

    // Define the navigation items to ensure consistent IDs and labels
    const navItems = [
        { id: 'eventmanagement', label: 'Event Management', icon: '🗓️' },
        { id: 'competitionmanagement', label: 'Competition Management', icon: '🏁' },
        { id: 'scoringcomponentmanagement', label: 'Scoring Components', icon: '🧮' },
        { id: 'competitormanagement', label: 'Competitor Management', icon: '👤' },
        { id: 'teammanagement', label: 'Team Management', icon: '👥' },
        { id: 'competitionregistration', label: 'Competition Registration', icon: '📝' },
        { id: 'leaderboardmanagement', label: 'Leaderboard Management', icon: '🏆' },
        { id: 'rollupleaderboardmanagement', label: 'Rollup Leaderboard Mgmt.', icon: '📊' },
        { id: 'adminleaderboards', label: 'View All Leaderboards', icon: '📋' },
        { id: 'adminusermanagement', label: 'User Management', icon: '🛠️' }
    ];

    // Filter navigation based on jobTitle
    let visibleNavItems = [];
    if (jobTitle === 'admin') {
        visibleNavItems = navItems;
    } else if (jobTitle === 'scorekeeper') {
        visibleNavItems = navItems.filter(item => ['competitormanagement','competitionregistration', 'teammanagement'].includes(item.id));
    }

    const activeLabel = visibleNavItems.find(item => item.id === activeSection)?.label || 'Welcome to ScoreKeep Admin';

    return (
        <div className="admin-layout">
            {isMobile && isSidebarOpen && (
                <div className="admin-overlay" onClick={toggleSidebar} aria-hidden="true" />
            )}

            {/* Sidebar Navigation */}
            <nav
                className={`admin-sidebar ${isSidebarOpen ? 'is-open' : 'is-closed'} ${isMobile ? 'is-mobile' : ''}`}
                aria-label="Admin navigation"
            >
                <div className="admin-sidebar-header">
                    <button type="button" className="admin-logo-button" onClick={handleHomeClick} aria-label="Go to home">
                        <img
                            src="/UncompressedLogos/ScoreKeepShield.webp"
                            alt="ScoreKeep Shield"
                            className="admin-sidebar-shield"
                        />
                        <img
                            src="/ScoreKeepFull.webp"
                            alt="ScoreKeep Logo"
                            className="admin-sidebar-logo"
                        />
                    </button>
                </div>
                <ul>
                    <li onClick={handleHomeClick} style={{ cursor: 'pointer' }} title="Home Page">
                        <span className="nav-icon" aria-hidden="true">🏠</span>
                        <span className="nav-label">Home Page</span>
                    </li>
                    {visibleNavItems.map(item => (
                        <li
                            key={item.id}
                            onClick={() => handleNavClick(item.id)}
                            className={activeSection === item.id ? 'active' : ''}
                            style={{ cursor: 'pointer' }}
                            title={item.label}
                        >
                            <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                            <span className="nav-label">{item.label}</span>
                        </li>
                    ))}
                    <li onClick={handleLogout} className="logout" style={{ cursor: 'pointer' }} title="Logout">
                        <span className="nav-icon" aria-hidden="true">⏻</span>
                        <span className="nav-label">Logout</span>
                    </li>
                </ul>
            </nav>

            {/* Main Content Area */}
            <div className={`admin-content ${isSidebarOpen && !isMobile ? 'with-sidebar' : ''} ${!isSidebarOpen && !isMobile ? 'with-collapsed' : ''} ${isMobile ? 'full' : ''}`}>
                <div className="admin-header">
                    <button
                        type="button"
                        className="icon-button"
                        onClick={toggleSidebar}
                        aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                        aria-expanded={isSidebarOpen}
                    >
                        {isSidebarOpen ? '◀' : '▶'}
                    </button>
                    <div className="admin-header-title">Admin &gt; {activeLabel}</div>
                </div>
                {activeSection === 'eventmanagement' && <EventManagement />}
                {activeSection === 'competitionmanagement' && <CompetitionManagement />}
                {activeSection === 'scoringcomponentmanagement' && <ScoringComponentManagement />}
                {activeSection === 'competitormanagement' && <CompetitorManagement />}
                {activeSection === 'teammanagement' && <TeamManagement />}
                {activeSection === 'competitionregistration' && <CompetitionRegistration />} {/* Add the new component */}
                {activeSection === 'leaderboardmanagement' && <LeaderboardManagement />}
                {activeSection === 'rollupleaderboardmanagement' && <RollupLeaderboardManagement />}
                {activeSection === 'adminleaderboards' && <AdminLeaderboards />}
                {activeSection === 'adminusermanagement' && <AdminUserManagement />}
            </div>
        </div>
    );
}
